import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';
import AddTeachersToBatchModal from '../components/batches/AddTeachersToBatchModal';
import AssignStudentsModal from '../components/teachers/AssignStudentsModal';

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

function SubjectChips({ subjects }) {
  if (!subjects || subjects.length === 0) return <span className="text-xs text-teacher-text-tertiary">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {subjects.map(s => (
        <span key={s} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
          {SUBJECT_LABEL[s] || s}
        </span>
      ))}
    </div>
  );
}

export default function BatchDetail() {
  const { batchId } = useParams();
  const [batch, setBatch] = useState(null);
  const [center, setCenter] = useState(null);
  const [teacherDocs, setTeacherDocs] = useState([]);
  const [studentDocs, setStudentDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddTeachers, setShowAddTeachers] = useState(false);
  const [showAddStudents, setShowAddStudents] = useState(false);

  async function load() {
    try {
      setLoading(true);
      const result = await api.getBatch(batchId);
      setBatch(result.data);

      // Load center for context
      if (result.data.center_id) {
        try {
          const c = await api.getCoachingCenter(result.data.center_id);
          setCenter(c.data);
        } catch { /* non-fatal */ }
      }

      // Load teacher docs (only the ones in this batch)
      const tIds = result.data.teacher_ids || [];
      const teachers = [];
      for (const tid of tIds) {
        try {
          const t = await api.getTeacher(tid);
          teachers.push(t.data);
        } catch { /* skip */ }
      }
      setTeacherDocs(teachers);

      // Hydrate student rows in parallel (~60 max in pilot scale)
      const sIds = result.data.student_ids || [];
      const hydrated = await Promise.all(sIds.map(async (sid) => {
        try {
          const u = await api.getUserDetails(sid);
          return { id: sid, ...u.data };
        } catch {
          // Student doc missing — surface as a stale entry the admin can clean up
          return { id: sid, missing: true };
        }
      }));
      setStudentDocs(hydrated);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [batchId]);

  async function handleRemoveTeacher(teacherId) {
    if (!confirm('Remove this teacher from the batch?')) return;
    try {
      await api.removeTeacherFromBatch(batchId, teacherId);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRemoveStudent(studentId) {
    if (!confirm('Remove this student from the batch?')) return;
    try {
      await api.removeStudentFromBatch(batchId, studentId);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleArchive() {
    const action = batch.is_active === false ? 'reactivate' : 'archive';
    if (!confirm(`${action} this batch?`)) return;
    try {
      await api.updateBatch(batchId, { is_active: batch.is_active === false });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  function formatDate(d) {
    if (!d) return 'Never';
    try { return formatDistanceToNow(new Date(d), { addSuffix: true }); }
    catch { return 'Unknown'; }
  }

  if (loading) {
    return <div className="p-6"><div className="text-center py-12">Loading batch...</div></div>;
  }
  if (error || !batch) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Batch not found'}
        </div>
      </div>
    );
  }

  const isArchived = batch.is_active === false;

  return (
    <div className="p-6">
      <nav className="mb-6">
        <Link to="/batches" className="text-primary-600 hover:text-primary-800">
          ← Back to Batches
        </Link>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-teacher-text-primary mb-2">{batch.name}</h1>
            <div className="space-y-1 text-sm text-teacher-text-secondary">
              {center && (
                <div>🏫 <Link to={`/coaching-centers/${center.id}`} className="text-primary-600 hover:underline">{center.name}</Link></div>
              )}
              <div>📚 Classes: {(batch.classes || []).join(', ') || '—'}</div>
              {batch.academic_year && <div>📅 {batch.academic_year}</div>}
            </div>
          </div>
          <button onClick={handleArchive}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              isArchived
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}>
            {isArchived ? 'Reactivate' : 'Archive'}
          </button>
        </div>
        <div className="mt-6 pt-6 border-t border-teacher-border grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-teacher-text-secondary mb-1">Status</div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              isArchived ? 'bg-teacher-bg-secondary text-teacher-text-secondary' : 'bg-green-100 text-green-700'
            }`}>{isArchived ? 'Archived' : 'Active'}</span>
          </div>
          <div>
            <div className="text-xs text-teacher-text-secondary mb-1">Teachers</div>
            <div className="text-2xl font-bold text-primary-600">{(batch.teacher_ids || []).length}</div>
          </div>
          <div>
            <div className="text-xs text-teacher-text-secondary mb-1">Students</div>
            <div className="text-2xl font-bold text-primary-600">{batch.total_students || 0}</div>
          </div>
          <div>
            <div className="text-xs text-teacher-text-secondary mb-1">Created</div>
            <div className="text-sm text-teacher-text-primary">{formatDate(batch.created_at)}</div>
          </div>
        </div>
      </div>

      {/* Teachers section */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border mb-6 overflow-hidden">
        <div className="p-6 border-b border-teacher-border flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-teacher-text-primary">Teachers in this batch</h2>
            <p className="text-xs text-teacher-text-secondary mt-1">Typically one teacher per subject</p>
          </div>
          {!isArchived && (
            <button onClick={() => setShowAddTeachers(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm">
              + Add Teacher
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teacher-bg border-b border-teacher-border">
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Subjects</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">View as Teacher</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {teacherDocs.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-8 text-center text-teacher-text-secondary">No teachers in this batch yet</td></tr>
              ) : (
                teacherDocs.map(t => (
                  <tr key={t.id} className="hover:bg-teacher-bg">
                    <td className="px-6 py-3 text-sm font-medium text-teacher-text-primary">{t.first_name} {t.last_name}</td>
                    <td className="px-6 py-3 text-sm text-teacher-text-secondary">{t.email}</td>
                    <td className="px-6 py-3"><SubjectChips subjects={t.subjects} /></td>
                    <td className="px-6 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(t.subjects || []).map(s => (
                          <Link
                            key={s}
                            to={`/teachers/${t.id}/batches/${batchId}/${s}`}
                            className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 px-2 py-1 rounded"
                            title={`View this batch as ${t.first_name} for ${SUBJECT_LABEL[s]}`}
                          >
                            {SUBJECT_LABEL[s]} →
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <button onClick={() => handleRemoveTeacher(t.id)}
                        className="text-red-600 hover:text-red-800">Remove</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Students section */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border overflow-hidden">
        <div className="p-6 border-b border-teacher-border flex justify-between items-center">
          <h2 className="text-lg font-semibold text-teacher-text-primary">Students ({studentDocs.length})</h2>
          {!isArchived && (
            <button onClick={() => setShowAddStudents(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm">
              + Add Students
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          {studentDocs.length === 0 ? (
            <div className="px-6 py-8 text-center text-teacher-text-secondary">No students in this batch yet</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-teacher-bg border-b border-teacher-border">
                  <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Last Active</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {studentDocs.map(s => {
                  if (s.missing) {
                    return (
                      <tr key={s.id} className="hover:bg-teacher-bg bg-red-50/30">
                        <td colSpan="4" className="px-6 py-3 text-sm text-red-700">
                          <span className="font-mono text-xs">{s.id}</span>
                          <span className="ml-2 text-xs">— student record missing (stale)</span>
                        </td>
                        <td className="px-6 py-3 text-sm">
                          <button onClick={() => handleRemoveStudent(s.id)}
                            className="text-red-600 hover:text-red-800">Remove</button>
                        </td>
                      </tr>
                    );
                  }
                  const p = s.profile || {};
                  const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || '—';
                  return (
                    <tr key={s.id} className="hover:bg-teacher-bg">
                      <td className="px-6 py-3 text-sm font-medium text-teacher-text-primary">
                        <Link to={`/users/${s.id}`} className="text-primary-600 hover:underline">{name}</Link>
                      </td>
                      <td className="px-6 py-3 text-sm text-teacher-text-secondary">{p.email || '—'}</td>
                      <td className="px-6 py-3 text-sm text-teacher-text-secondary">{p.phone || '—'}</td>
                      <td className="px-6 py-3 text-sm text-teacher-text-secondary">{formatDate(p.lastActive)}</td>
                      <td className="px-6 py-3 text-sm">
                        <button onClick={() => handleRemoveStudent(s.id)}
                          className="text-red-600 hover:text-red-800">Remove</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <AddTeachersToBatchModal
        isOpen={showAddTeachers}
        onClose={() => setShowAddTeachers(false)}
        batchId={batchId}
        centerId={batch.center_id}
        existingTeacherIds={batch.teacher_ids || []}
        onSuccess={load}
      />

      <AssignStudentsModal
        isOpen={showAddStudents}
        onClose={() => setShowAddStudents(false)}
        batchId={batchId}
        onSuccess={load}
      />
    </div>
  );
}
